import React, { useState, useEffect, useContext } from "react";
import "./index.scss";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { HOSTNAME, REST_URLS } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import {
  getFileNameFromUrl,
  PAGE_LIMIT,
  getFileExtensionFromUrl,
} from "../../../utils";
import CustomButtonComponent from "../../../core/custom-button";
import DeleteModal from "../../../components/delete-modal";
import FormGenerator from "../../../core/form-generator";
import { getKnowledgeSharingFormConfig } from "./config";
import { handleFormValidation } from "../../../utils";
import SiFileUpload from "../../../core/file-uploader";
import TrainingMonthEnrollmentAdmin from "../../oem/training/training-month-enrollment/training-month-enrollment-admin";
import AppContext from "../../../utils/context";
import { Icon } from "@iconify/react";
import moment from "moment";
import CustomModal from "../../../core/modal";

const TrainingModule = () => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [editData, setEditData] = useState({});
  const [isDelete, setIsDelete] = useState({ isOpen: false, data: {} });
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50 || PAGE_LIMIT,
    sortBy: "-createdAt",
  });
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const financialYearStart = currentMonth >= 3 ? currentYear : currentYear - 1;
  const financialYearEnd = financialYearStart + 1;
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [scheduleResponse, setScheduleResponse] = useState([]);
  const [moduleDetails, setModuleDetails] = useState({
    isOpen: false,
    data: {},
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const { userDetails = {} } = useContext(AppContext);

  const handleDownload = async (ele) => {
    setIsDownloading(true);
    try {
      const response = await fetch(ele?.attachment);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${ele?.name}${
        getFileExtensionFromUrl(ele?.attachment) || ".pdf"
      }`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Download started successfully");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEdit = (data) => {
    setModuleDetails({ isOpen: false, data: {} });
    setTimeout(() => {
      setIsCreate(true);
      setEditData(data);
      setFormData({ name: data.name });
      setFiles([{ url: data.attachment }]);
    }, 300);
  };

  const handleDelete = (data) => {
    setModuleDetails({ isOpen: false, data: {} });
    setTimeout(() => {
      setIsDelete({ isOpen: true, data: data });
    }, 300);
  };

  const deleteHandler = () => {
    invokeApi(
      HTTP_METHODS.DELETE,
      `${HOSTNAME}${REST_URLS.TRAINING_MODULE}/${isDelete?.data?.id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Deleted Successfully");
          setIsDelete({ isOpen: false, data: {} });
          loadData(filters);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to delete knowledge");
      });
  };

  const loadData = (filters) => {
    setIsLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.TRAINING_MODULE}`,
      null,
      filters
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          const result = res?.results?.map((ele, index) => ({
            ...ele,
            siNo: index + 1,
          }));
          setData({ ...res, results: result });
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load Training Modules");
      });
  };

  const submitHandler = () => {
    const isValid = handleFormValidation(
      getKnowledgeSharingFormConfig(),
      setErrors,
      formData
    );
    if (!files[0]) {
      toast.error("Document is required");
      return;
    }

    if (isValid && files[0]) {
      const formDataPayload = {
        name: formData.name,
        attachment: files[0]?.url,
      };

      if (editData?.id) {
        invokeApi(
          HTTP_METHODS.PUT,
          `${HOSTNAME}${REST_URLS.TRAINING_MODULE}/${editData.id}`,
          formDataPayload
        )
          .then((res) => {
            if (res.message) {
              toast.error(res.message);
            } else {
              toast.success("Module updated successfully");
              setIsCreate(false);
              setEditData({});
              setFormData({});
              setFiles([]);
              loadData({ ...filters, page: 1 });
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error("Failed to update module");
          });
      } else {
        invokeApi(
          HTTP_METHODS.POST,
          `${HOSTNAME}${REST_URLS.TRAINING_MODULE}`,
          formDataPayload
        )
          .then((res) => {
            if (res.message) {
              toast.error(res.message);
            } else {
              toast.success("Knowledge shared successfully");
              setIsCreate(false);
              setFormData({});
              setFiles([]);
              loadData({ ...filters, page: 1 });
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error("Failed to share knowledge");
          });
      }
    }
  };

  const callBack = (newFile) => {
    setFiles([newFile]);
  };

  const loadTrainings = () => {
    invokeApi(
      HTTP_METHODS.GET,
      userDetails.role === "ADMIN"
        ? `${HOSTNAME}${REST_URLS.GET_COURSE_DATES_LIST}?startYear=${financialYearStart}&endYear=${financialYearEnd}`
        : `${HOSTNAME}${
            REST_URLS.GET_TRAINING_DATES_LIST
          }?startYear=${financialYearStart}&endYear=${financialYearStart + 1}`,
      null
    ).then((response) => {
      if (response) {
        setScheduleResponse(response);
      }
    });
  };

  useEffect(() => {
    loadData(filters);
    loadTrainings();
  }, []);

  return (
    <div className="page-body">
      <div className="view-detailed-ticket">
        <div>
          <p className="tab-name">Admin</p>
          <p className="section-name">Training Module</p>
        </div>
      </div>
      <div>
        <div className="create-con">
          <p className="primary-text">Training Module</p>
          <div>
            <CustomButtonComponent
              variant="contained"
              color="primary"
              onClick={() => {
                setIsCreate(true);
                setEditData({});
                setFormData({});
                setFiles([]);
              }}
            >
              Create Training Module
            </CustomButtonComponent>
          </div>
        </div>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Card sx={{ mb: 3, height: "100%" }}>
              <CardContent>
                {isLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : data?.results?.length > 0 ? (
                  <List>
                    {data.results.map((module, index) => (
                      <React.Fragment key={module.id}>
                        <ListItem
                          disablePadding
                          onClick={() =>
                            setModuleDetails({ isOpen: true, data: module })
                          }
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              className="primary-text"
                              sx={{
                                fontWeight: 600,
                                fontSize: "1rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                marginBottom: "5px",
                                "&:hover": {
                                  transform: "translateX(1px)",
                                },
                              }}
                            >
                              {index + 1}. {module?.name}
                            </Typography>
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="h6"
                    sx={{ textAlign: "center", py: 4, color: "gray" }}
                  >
                    No Training Modules Found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  className="primary-text"
                  variant="h6"
                  sx={{ mb: 1 }}
                >
                  Duration
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>Modules 1 to 5</Typography>
                <Typography>3 Days-Hands- On Training</Typography>
                <Typography>1 Day- On-Line training</Typography>
                <Typography sx={{ fontWeight: 600, mt: 2 }}>
                  Modules 6 to 12
                </Typography>
                <Grid container sx={{ mb: 1 }}>
                  <Grid item md={2}>
                    <Typography sx={{ fontWeight: 600 }}> Place:</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>
                      Chennai Anna Salai Plant - Service Tech Center.
                    </Typography>
                    <Typography>OEM Prefered Location On - demand</Typography>
                  </Grid>
                </Grid>
                <Grid container sx={{ mb: 1 }}>
                  <Grid item md={2}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {" "}
                      Objective :{" "}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography>
                      To make participants competent enough to carry out quality
                      service on Diesel Engines.
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item md={2}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {" "}
                      Course Content :{" "}
                    </Typography>
                  </Grid>
                  <Grid item md={10}>
                    <Typography>
                      Technical specification, Working Principles, DO's and
                      Don'ts, Dismantling / Assembling and Troubleshooting.
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <TrainingMonthEnrollmentAdmin
            isModule={true}
            scheduleResponse={scheduleResponse}
          />
        </Box>
      </div>

      {/* PDF Viewer Modal */}
      {moduleDetails?.isOpen && (
        <CustomModal
          onClose={() => setModuleDetails({ isOpen: false, data: {} })}
          title={moduleDetails?.data?.name}
          maxWidth="lg"
        >
          <Grid container sx={{ mt: 2 }}>
            <Grid item xs={12}>
              {/* Header with date, edit, delete, and download buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  pb: 2,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2" color="gray">
                  Added on{" "}
                  <Typography component="span" className="primary-text">
                    {moment(moduleDetails?.data?.createdAt).format(
                      "DD MMMM YYYY"
                    )}
                  </Typography>
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    onClick={() => handleEdit(moduleDetails.data)}
                    sx={{
                      backgroundColor: "#f59e0b",
                      color: "white",
                      padding: "8px",
                      "&:hover": {
                        backgroundColor: "#d97706",
                      },
                    }}
                  >
                    <Icon icon="mdi:pencil" width="1.2rem" height="1.2rem" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(moduleDetails.data)}
                    sx={{
                      backgroundColor: "#ef4444",
                      color: "white",
                      padding: "8px",
                      "&:hover": {
                        backgroundColor: "#dc2626",
                      },
                    }}
                  >
                    <Icon icon="mdi:delete" width="1.2rem" height="1.2rem" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDownload(moduleDetails.data)}
                    disabled={isDownloading}
                    sx={{
                      backgroundColor: "#095adc",
                      color: "white",
                      padding: "8px",
                      "&:hover": {
                        backgroundColor: "#074ab8",
                      },
                      "&:disabled": {
                        backgroundColor: "#ccc",
                        color: "white",
                      },
                    }}
                  >
                    {isDownloading ? (
                      <Icon
                        icon="eos-icons:loading"
                        width="1.2rem"
                        height="1.2rem"
                      />
                    ) : (
                      <Icon
                        icon="tabler:download"
                        width="1.2rem"
                        height="1.2rem"
                      />
                    )}
                  </IconButton>
                </Box>
              </Box>

              {/* PDF Viewer */}
              <Box
                sx={{
                  width: "100%",
                  height: "70vh",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <iframe
                  src={`${moduleDetails?.data?.attachment}#toolbar=0`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title={moduleDetails?.data?.name}
                />
              </Box>
            </Grid>
          </Grid>
        </CustomModal>
      )}

      {/* Create/Edit Modal */}
      <Dialog
        open={isCreate}
        onClose={() => {
          setIsCreate(false);
          setEditData({});
          setFormData({});
          setFiles([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editData?.id ? "Edit Knowledge" : "Share Knowledge"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Box flex={1}>
              <FormGenerator
                config={getKnowledgeSharingFormConfig()}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                fieldGridSize={12}
              />
            </Box>
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <label style={{ fontWeight: 800, marginBottom: 8 }}>
                Agenda *
              </label>
              <Box display="flex" alignItems="center" gap={1}>
                <SiFileUpload
                  id={null}
                  callBack={callBack}
                  fileTypes={[".pdf", "application/pdf"]}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            {files[0] && (
              <a href={files[0]?.url} target="_blank">
                {getFileNameFromUrl(files[0]?.url, 50)}
              </a>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <CustomButtonComponent
            variant="outlined"
            onClick={() => {
              setIsCreate(false);
              setEditData({});
              setFormData({});
              setFiles([]);
            }}
          >
            Cancel
          </CustomButtonComponent>
          <CustomButtonComponent
            variant="contained"
            color="success"
            onClick={submitHandler}
          >
            {editData?.id ? "Update" : "Submit"}
          </CustomButtonComponent>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      {isDelete?.isOpen && (
        <DeleteModal
          deleteHandler={deleteHandler}
          onClose={() => {
            setIsDelete({ isOpen: false, data: {} });
          }}
        />
      )}
    </div>
  );
};

export default TrainingModule;
